/**
 * AssistanceDataFields — the assistance service block of the studios' general
 * template form: loungeName ("Megnevezés"), loungeCode ("Kód"),
 * accessFrom/accessTo, 24/7 hotline, and the optional related link.
 * Presentational — the parent owns useForm and passes `register`/`errors`.
 * Every schema-validated field renders its inline error (same idiom as
 * RecipientFields/TravelDataFields), so a rejected submit is never a silent
 * dead-end.
 */

import type { FieldErrors, UseFormRegister } from 'react-hook-form';

import { useT } from '../../i18n';

export interface AssistanceFieldValues {
  loungeName: string;
  loungeCode: string;
  accessFrom: string;
  accessTo: string;
  assistancePhone: string;
  policyNumber?: string;
  esimLinkUrl?: string;
}

export interface AssistanceDataFieldsProps<T extends AssistanceFieldValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
}

export function AssistanceDataFields<T extends AssistanceFieldValues>({
  register,
  errors,
}: AssistanceDataFieldsProps<T>): JSX.Element {
  const t = useT();
  const loungeNameError = errors.loungeName?.message as string | undefined;
  const loungeCodeError = errors.loungeCode?.message as string | undefined;
  const accessFromError = errors.accessFrom?.message as string | undefined;
  const accessToError = errors.accessTo?.message as string | undefined;
  const phoneError = errors.assistancePhone?.message as string | undefined;
  const policyError = errors.policyNumber?.message as string | undefined;
  const esimError = errors.esimLinkUrl?.message as string | undefined;
  return (
    <div className="border-t border-slate-200 pt-4 space-y-3">
      <h2 className="text-sm font-semibold text-slate-700">{t('form.assistanceSection')}</h2>
      <div className="grid grid-cols-[1fr_120px] gap-3">
        <div>
          <label className="label">{t('form.loungeName')}</label>
          <input
            {...register('loungeName' as never)}
            placeholder={t('form.loungeNamePlaceholder')}
            className="input mt-1"
          />
          {loungeNameError ? (
            <div className="text-xs text-red-600 mt-1">{loungeNameError}</div>
          ) : null}
        </div>
        <div>
          <label className="label">{t('form.loungeCode')}</label>
          <input
            {...register('loungeCode' as never)}
            placeholder="IW"
            className="input mt-1 font-mono uppercase"
          />
          {loungeCodeError ? (
            <div className="text-xs text-red-600 mt-1">{loungeCodeError}</div>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">{t('form.accessFrom')}</label>
          <input type="date" {...register('accessFrom' as never)} className="input mt-1" />
          {accessFromError ? (
            <div className="text-xs text-red-600 mt-1">{accessFromError}</div>
          ) : null}
        </div>
        <div>
          <label className="label">{t('form.accessTo')}</label>
          <input type="date" {...register('accessTo' as never)} className="input mt-1" />
          {accessToError ? <div className="text-xs text-red-600 mt-1">{accessToError}</div> : null}
        </div>
      </div>
      <div>
        <label className="label">{t('form.policyNumberOptional')}</label>
        <input
          {...register('policyNumber' as never)}
          placeholder={t('form.policyNumberPlaceholder')}
          className="input mt-1 font-mono"
        />
        {policyError ? <div className="text-xs text-red-600 mt-1">{policyError}</div> : null}
      </div>
      <div>
        <label className="label">{t('form.hotline')}</label>
        <input
          type="tel"
          {...register('assistancePhone' as never)}
          placeholder="+36 1 234 5678"
          className="input mt-1"
        />
        {phoneError ? <div className="text-xs text-red-600 mt-1">{phoneError}</div> : null}
      </div>
      <div>
        <label className="label">{t('form.relatedLinkOptional')}</label>
        <input
          type="url"
          placeholder="https://partner.example.com/asszisztencia"
          {...register('esimLinkUrl' as never)}
          className="input mt-1 font-mono text-xs"
        />
        {esimError ? <div className="text-xs text-red-600 mt-1">{esimError}</div> : null}
      </div>
    </div>
  );
}
